import React, { useState, useEffect } from 'react';
import { TYPES_OPERATIONS, PROFILS_BIENS, getEquipementsForProfil } from '../../configs/contactEquipementsConfig';

/**
 * Formulaire pour une recherche individuelle
 * Utilisé dans MultipleSearchesManager
 */
const SingleSearchForm = ({ 
  recherche, 
  onUpdate,
  onToggleEquipement 
}) => {
  const [tempVille, setTempVille] = useState('');
  const [tempSecteur, setTempSecteur] = useState('');
  const [villeSuggestions, setVilleSuggestions] = useState([]);
  const [showVilleSuggestions, setShowVilleSuggestions] = useState(false);
  const [isSearchingVille, setIsSearchingVille] = useState(false);

  // Obtenir les équipements disponibles selon le profil
  const availableEquipements = recherche.profil ? getEquipementsForProfil(recherche.profil) : [];

  // Handler générique pour mettre à jour un champ
  const handleChange = (field, value) => {
    onUpdate({ ...recherche, [field]: value });
  };

  // Recherche de villes via API
  const searchVilles = async (query) => {
    if (query.length < 3) {
      setVilleSuggestions([]);
      setShowVilleSuggestions(false);
      return;
    }

    setIsSearchingVille(true);
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&type=municipality&limit=8`
      );
      const data = await response.json();
      const villes = data.features.map(f => f.properties.city).filter((v, i, arr) => arr.indexOf(v) === i);
      setVilleSuggestions(villes);
      setShowVilleSuggestions(villes.length > 0);
    } catch (error) {
      console.error('Erreur recherche ville:', error);
    } finally {
      setIsSearchingVille(false);
    }
  };

  const handleVilleInputChange = (e) => {
    const value = e.target.value;
    setTempVille(value);
    
    clearTimeout(window.villeSearchTimeout);
    window.villeSearchTimeout = setTimeout(() => {
      searchVilles(value);
    }, 300);
  };

  const addVille = (ville = null) => {
    const villeToAdd = ville || tempVille.trim();
    if (!villeToAdd) return;
    
    const currentVilles = recherche.villes || [];
    if (!currentVilles.includes(villeToAdd)) {
      handleChange('villes', [...currentVilles, villeToAdd]);
    }
    setTempVille('');
    setShowVilleSuggestions(false);
  };

  const removeVille = (ville) => {
    const currentVilles = recherche.villes || [];
    handleChange('villes', currentVilles.filter(v => v !== ville));
  };

  const addSecteur = () => {
    const secteur = tempSecteur.trim();
    if (!secteur) return;
    
    const currentSecteurs = recherche.secteurs || [];
    if (!currentSecteurs.includes(secteur)) {
      handleChange('secteurs', [...currentSecteurs, secteur]);
    }
    setTempSecteur('');
  };

  const removeSecteur = (secteur) => {
    const currentSecteurs = recherche.secteurs || [];
    handleChange('secteurs', currentSecteurs.filter(s => s !== secteur));
  };

  const toggleEquipement = (equipId) => {
    const currentEquipements = recherche.equipements_souhaites || {};
    const newEquipements = { ...currentEquipements };
    
    if (newEquipements[equipId]) {
      delete newEquipements[equipId];
    } else {
      newEquipements[equipId] = true;
    }
    
    handleChange('equipements_souhaites', newEquipements);
  };

  const currentOperation = recherche.operation;
  const isLocationDroitEntree = currentOperation === 'location_droit_entree';
  const isLocation = currentOperation === 'location' || currentOperation === 'location_droit_entree' || currentOperation === 'location_gerance';

  return (
    <div className="space-y-4">
      {/* Label personnalisable */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
          Nom de la recherche
        </label>
        <input 
          type="text"
          placeholder="Ex: Restaurant Paris centre"
          className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
          value={recherche.label || ''}
          onChange={(e) => handleChange('label', e.target.value)}
        />
      </div>

      {/* Type d'opération */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Type d'opération <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {TYPES_OPERATIONS.map((op) => (
            <label 
              key={op.value}
              className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition ${
                recherche.operation === op.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-blue-300'
              }`}
            >
              <input 
                type="radio" 
                name={`operation-${recherche.id}`}
                className="w-4 h-4 text-blue-600"
                checked={recherche.operation === op.value}
                onChange={() => handleChange('operation', op.value)}
              />
              <span className="text-sm font-medium text-slate-700">{op.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Profil de bien */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Profil de bien recherché <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {PROFILS_BIENS.map((profil) => (
            <label 
              key={profil.value}
              className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition ${
                recherche.profil === profil.value
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-green-300'
              }`}
            >
              <input 
                type="radio" 
                name={`profil-${recherche.id}`}
                className="w-4 h-4 text-green-600"
                checked={recherche.profil === profil.value}
                onChange={() => handleChange('profil', profil.value)}
              />
              <span className="text-sm font-medium text-slate-700">{profil.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Budget et Surface */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            {isLocation ? 'Loyer minimum (€/mois)' : 'Budget minimum (€)'}
          </label>
          <input 
            type="number" 
            placeholder="0"
            className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
            value={recherche.budget_min || ''}
            onChange={(e) => handleChange('budget_min', e.target.value ? parseFloat(e.target.value) : null)}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            {isLocation ? 'Loyer maximum (€/mois)' : 'Budget maximum (€)'}
          </label>
          <input 
            type="number" 
            placeholder={isLocation ? '5000' : '350000'}
            className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
            value={recherche.budget_max || ''}
            onChange={(e) => handleChange('budget_max', e.target.value ? parseFloat(e.target.value) : null)}
          />
        </div>
        
        {/* Droit d'entrée pour location avec pas-de-porte */}
        {isLocationDroitEntree && (
          <>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Droit d'entrée minimum (€)
                <span className="text-xs text-slate-500 font-normal ml-2">(Pas-de-porte)</span>
              </label>
              <input 
                type="number" 
                placeholder="0"
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
                value={recherche.droit_entree_min || ''}
                onChange={(e) => handleChange('droit_entree_min', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Droit d'entrée maximum (€)
                <span className="text-xs text-slate-500 font-normal ml-2">(Pas-de-porte)</span>
              </label>
              <input 
                type="number" 
                placeholder="100000"
                className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
                value={recherche.droit_entree_max || ''}
                onChange={(e) => handleChange('droit_entree_max', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </div>
          </>
        )}
        
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Surface minimum (m²)</label>
          <input 
            type="number" 
            placeholder="0"
            className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
            value={recherche.surface_min || ''}
            onChange={(e) => handleChange('surface_min', e.target.value ? parseFloat(e.target.value) : null)}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Surface maximum (m²)</label>
          <input 
            type="number" 
            placeholder="200"
            className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
            value={recherche.surface_max || ''}
            onChange={(e) => handleChange('surface_max', e.target.value ? parseFloat(e.target.value) : null)}
          />
        </div>
      </div>

      {/* Villes et Secteurs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Villes recherchées
          </label>
          <div className="flex flex-wrap gap-2 p-3 border-2 border-slate-200 rounded-lg mb-2 min-h-[60px] bg-slate-50">
            {(recherche.villes || []).map((ville, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                <i className="bi bi-geo-alt-fill text-xs"></i>
                {ville}
                <button type="button" onClick={() => removeVille(ville)} className="hover:text-blue-900">
                  <i className="bi bi-x"></i>
                </button>
              </span>
            ))}
          </div>
          <div className="relative">
            <div className="flex gap-2">
              <input 
                type="text" 
                placeholder="Rechercher une ville..."
                className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
                value={tempVille}
                onChange={handleVilleInputChange}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVille())}
              />
              <button 
                type="button"
                onClick={() => addVille()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
              >
                <i className="bi bi-plus-lg"></i>
              </button>
            </div>
            
            {showVilleSuggestions && villeSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border-2 border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {villeSuggestions.map((ville, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                    onClick={() => addVille(ville)}
                  >
                    <i className="bi bi-geo-alt mr-2 text-blue-500"></i>
                    {ville}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Secteurs souhaités
          </label>
          <div className="flex flex-wrap gap-2 p-3 border-2 border-slate-200 rounded-lg mb-2 min-h-[60px] bg-slate-50">
            {(recherche.secteurs || []).map((secteur, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                {secteur}
                <button type="button" onClick={() => removeSecteur(secteur)} className="hover:text-blue-900">
                  <i className="bi bi-x"></i>
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input 
              type="text" 
              placeholder="Ex: Centre-ville"
              className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
              value={tempSecteur}
              onChange={(e) => setTempSecteur(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSecteur())}
            />
            <button 
              type="button"
              onClick={addSecteur}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
            >
              <i className="bi bi-plus-lg"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Équipements */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Équipements souhaités
        </label>
        {recherche.profil && availableEquipements.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {availableEquipements.map((equip) => (
              <label 
                key={equip.id}
                className={`flex items-center gap-2 p-2 border-2 rounded-lg cursor-pointer transition ${
                  recherche.equipements_souhaites?.[equip.id]
                    ? 'border-green-500 bg-green-50'
                    : 'border-slate-200 hover:border-green-300'
                }`}
              >
                <input 
                  type="checkbox" 
                  className="w-4 h-4 text-green-600 rounded"
                  checked={!!recherche.equipements_souhaites?.[equip.id]}
                  onChange={() => toggleEquipement(equip.id)}
                />
                <span className="text-sm font-medium text-slate-700">
                  <span className="mr-1">{equip.icon}</span> {equip.label}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center text-slate-500 text-sm">
            <i className="bi bi-info-circle mr-2"></i>
            Sélectionnez un profil pour afficher les équipements
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
          Notes (optionnel)
        </label>
        <textarea 
          placeholder="Ex: Client très motivé, budget flexible si terrasse + parking"
          className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none resize-none"
          rows={3}
          value={recherche.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
        />
      </div>

      {/* Bouton de validation */}
      <div className="pt-4 border-t-2 border-slate-200">
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mb-3">
          <p className="text-sm text-blue-700 flex items-start gap-2">
            <i className="bi bi-info-circle-fill text-lg"></i>
            <span>
              Les modifications sont <strong>automatiquement enregistrées</strong> lorsque vous sauvegardez le contact (bouton "Enregistrer" en haut de la page).
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            // Replier la carte pour indiquer que c'est OK
            const header = document.querySelector(`[data-recherche-id="${recherche.id}"]`);
            if (header) header.click();
          }}
          className="w-full px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold transition flex items-center justify-center gap-2"
        >
          <i className="bi bi-check2"></i>
          Fermer cette recherche
        </button>
      </div>
    </div>
  );
};

export default SingleSearchForm;
